import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Upload, X, FileText, CheckCircle2, AlertCircle, Loader2, RefreshCw, Database, Zap } from 'lucide-react';
import { api } from '../../utils/api';

// The Brands page's heavyweight tool modals — CSV upload, bulk deep
// research, and force social sync — moved verbatim from the old
// pages/Brands.jsx card-grid page when Brands was rebuilt as the
// Organic table + detail pane. Only the export keyword changed.

export function CsvUploadModal({ onClose, onCompleted }) {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('select'); // select | uploading | running | done | error
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [parseWarnings, setParseWarnings] = useState([]);
  const fileInputRef = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const startUpload = async () => {
    if (!file) return;
    setPhase('uploading');
    setError(null);
    try {
      const initial = await api.uploadBrandsCsv(file);
      setParseWarnings(initial.parse_warnings || []);
      setJob({
        id: initial.job_id,
        status: initial.status,
        total_rows: initial.total_rows,
        processed_rows: 0,
        added: 0,
        enriched: 0,
        skipped: 0,
        errors: 0,
        summary: [],
      });
      setPhase('running');
      pollJob(initial.job_id);
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  };

  const pollJob = async (jobId) => {
    try {
      const status = await api.getCsvUploadJob(jobId);
      setJob(status);
      if (status.status === 'completed') {
        setPhase('done');
        if (onCompleted) onCompleted();
        return;
      }
      if (status.status === 'failed') {
        setPhase('error');
        setError(status.error_message || 'Job failed');
        return;
      }
      pollTimer.current = setTimeout(() => pollJob(jobId), 2000);
    } catch (err) {
      setError(err.message);
      setPhase('error');
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setError(null);
    }
  };

  const reset = () => {
    setFile(null);
    setJob(null);
    setError(null);
    setParseWarnings([]);
    setPhase('select');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary, #fff)', borderRadius: '12px',
          maxWidth: '640px', width: '92%', maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '1.25rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Upload size={20} /> Bulk Upload Brands
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {phase === 'select' && (
            <>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Upload a CSV with one row per brand. Required column: <code>name</code>.
                Optional: <code>category</code>, <code>website</code>, <code>instagram_handle</code>,
                <code>tiktok_handle</code>, <code>linkedin_url</code>, <code>youtube_url</code>,
                <code>country</code>, <code>hq_city</code>, <code>hq_state</code>,
                <code>founders</code>, <code>founded_year</code>, <code>revenue</code>, <code>funding</code>,
                <code>parent_company</code>, <code>notes</code>.
              </p>

              <div style={{ background: 'var(--bg-secondary, #f9fafb)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                <strong>How rows are processed:</strong>
                <ul style={{ margin: '0.5rem 0 0 1.25rem', paddingLeft: 0 }}>
                  <li>Row has an Instagram or TikTok handle → added directly. Kimi backfills any blank location / founders / founded_year / parent_company columns. Cron picks up metrics next Sunday.</li>
                  <li>Row has only a website → researched (same as the "Add from Website" form above).</li>
                  <li>Row has only a name → searched via Brave + Kimi, results land in the pending-review queue.</li>
                  <li><code>revenue</code> and <code>funding</code> are not auto-researched — supply them in the CSV if you have them.</li>
                </ul>
                <a
                  href={api.csvUploadTemplateUrl()}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.6rem', color: 'var(--primary, #667eea)', textDecoration: 'underline' }}
                >
                  <FileText size={14} /> Download sample CSV
                </a>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                style={{ marginBottom: '1rem' }}
              />
              {file && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!file}
                  onClick={startUpload}
                >
                  <Upload size={14} /> Upload &amp; Process
                </button>
              </div>
            </>
          )}

          {(phase === 'uploading' || phase === 'running') && job && (
            <UploadProgress job={job} parseWarnings={parseWarnings} />
          )}
          {phase === 'uploading' && !job && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
              <Loader2 size={18} className="spin" /> Uploading file…
            </div>
          )}

          {phase === 'done' && job && (
            <UploadResult
              job={job}
              parseWarnings={parseWarnings}
              onUploadAnother={reset}
              onClose={onClose}
            />
          )}

          {phase === 'error' && (
            <div>
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <AlertCircle size={18} /> {error}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
                <button className="btn btn-primary btn-sm" onClick={reset}>Try again</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function UploadProgress({ job, parseWarnings }) {
  const pct = job.total_rows ? Math.round((job.processed_rows / job.total_rows) * 100) : 0;
  return (
    <div>
      <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
        Processing {job.processed_rows} / {job.total_rows} rows…
      </div>
      <div style={{ height: '10px', background: 'var(--bg-secondary, #f3f4f6)', borderRadius: '999px', overflow: 'hidden', marginBottom: '1rem' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #667eea, #764ba2)', transition: 'width 0.3s' }} />
      </div>
      <CounterRow job={job} />
      {parseWarnings.length > 0 && <ParseWarnings warnings={parseWarnings} />}
    </div>
  );
}


function UploadResult({ job, parseWarnings, onUploadAnother, onClose }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: '#059669' }}>
        <CheckCircle2 size={20} /> Processed {job.total_rows} row{job.total_rows === 1 ? '' : 's'}
      </div>
      <CounterRow job={job} />
      {parseWarnings.length > 0 && <ParseWarnings warnings={parseWarnings} />}
      {job.enriched > 0 && (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            background: 'rgba(217,119,6,0.10)',
            border: '1px solid rgba(217,119,6,0.35)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: '#92400e' }}>
            <strong>{job.enriched}</strong> {job.enriched === 1 ? 'brand is' : 'brands are'} awaiting your approval.
          </span>
          <Link
            to="/brands/research/pending"
            className="btn btn-primary btn-sm"
            onClick={onClose}
          >
            Review pending
          </Link>
        </div>
      )}
      {job.summary && job.summary.length > 0 && (
        <div style={{ marginTop: '1rem', maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary, #f9fafb)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem 0.75rem' }}>Row</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Brand</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Status</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {job.summary.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
                  <td style={{ padding: '0.4rem 0.75rem' }}>{r.csv_row}</td>
                  <td style={{ padding: '0.4rem 0.75rem' }}>{r.name}</td>
                  <td style={{ padding: '0.4rem 0.75rem' }}>
                    <StatusBadge status={r.status} />
                  </td>
                  <td style={{ padding: '0.4rem 0.75rem', color: 'var(--text-secondary)' }}>
                    {r.status === 'enriched' ? (
                      <Link to="/brands/research/pending" onClick={onClose}>
                        {r.message || 'Awaiting approval'}
                      </Link>
                    ) : (
                      r.message
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={onUploadAnother}>Upload another</button>
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}


function CounterRow({ job }) {
  const cells = [
    { label: 'Added', value: job.added, color: '#059669' },
    { label: 'In Review', value: job.enriched, color: '#d97706' },
    { label: 'Skipped', value: job.skipped, color: '#6b7280' },
    { label: 'Errors', value: job.errors, color: '#b91c1c' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
      {cells.map((c) => (
        <div key={c.label} style={{ background: 'var(--bg-secondary, #f9fafb)', borderRadius: '8px', padding: '0.5rem 0.75rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: c.color }}>{c.value}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}


function StatusBadge({ status }) {
  const styles = {
    added: { background: 'rgba(16,185,129,0.15)', color: '#059669' },
    enriched: { background: 'rgba(217,119,6,0.15)', color: '#b45309' },
    skipped: { background: 'rgba(107,114,128,0.15)', color: '#4b5563' },
    error: { background: 'rgba(239,68,68,0.15)', color: '#b91c1c' },
  };
  const s = styles[status] || styles.skipped;
  return (
    <span style={{
      ...s,
      padding: '0.15rem 0.5rem',
      borderRadius: '999px',
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>{status}</span>
  );
}


function ParseWarnings({ warnings }) {
  return (
    <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.8rem', background: 'rgba(217,119,6,0.1)', color: '#92400e', borderRadius: '8px', fontSize: '0.8rem' }}>
      <strong>Parse warnings ({warnings.length}):</strong>
      <ul style={{ margin: '0.25rem 0 0 1.25rem', paddingLeft: 0 }}>
        {warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
        {warnings.length > 5 && <li>… and {warnings.length - 5} more</li>}
      </ul>
    </div>
  );
}


export function BulkResearchModal({ onClose, onCompleted }) {
  // Phases: 'preview' (counts + sample), 'running' (progress poll),
  // 'done' (final summary), 'error'.
  const [phase, setPhase] = useState('preview');
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.listIncompleteBrands()
      .then(data => { if (!cancelled) setPreview(data); })
      .catch(err => { if (!cancelled) setError(err?.message || 'Failed to load preview'); });
    return () => { cancelled = true; };
  }, []);

  // Poll the job row every 2 s while it's running; stop on terminal status.
  useEffect(() => {
    if (phase !== 'running' || !job?.job_id) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api.getBulkDeepResearchJob(job.job_id);
        if (cancelled) return;
        setJob({ ...next, job_id: job.job_id });
        if (next.status === 'completed' || next.status === 'failed') {
          setPhase(next.status === 'failed' ? 'error' : 'done');
          if (next.status === 'failed' && next.error_message) {
            setError(next.error_message);
          }
          if (onCompleted) onCompleted();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to poll job');
          setPhase('error');
        }
      }
    };
    const id = setInterval(tick, 2000);
    tick();  // first read immediately
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, job?.job_id, onCompleted]);

  const startRun = async (mode = 'fill') => {
    try {
      setError(null);
      const result = await api.startBulkDeepResearch(mode);
      setJob(result);
      setPhase('running');
    } catch (err) {
      setError(err?.message || 'Failed to start bulk run');
      setPhase('error');
    }
  };

  const total = preview?.total ?? 0;
  const totalAll = preview?.total_all ?? 0;
  const sample = preview?.sample ?? [];
  const orgId = preview?.org_id ?? job?.org_id ?? '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary, #fff)', borderRadius: '12px',
          maxWidth: '720px', width: '94%', maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '1.25rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Database size={20} /> Bulk Deep Research
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {phase === 'preview' && (
            <BulkPreview
              loading={!preview && !error}
              total={total}
              totalAll={totalAll}
              sample={sample}
              orgId={orgId}
              error={error}
              onClose={onClose}
              onStart={startRun}
            />
          )}

          {phase === 'running' && job && (
            <BulkProgress job={job} />
          )}

          {phase === 'done' && job && (
            <BulkResult job={job} onClose={onClose} />
          )}

          {phase === 'error' && (
            <div>
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <AlertCircle size={18} /> {error || 'Bulk run failed.'}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function BulkPreview({ loading, total, totalAll, sample, orgId, error, onClose, onStart }) {
  // Force-overwrite confirmation prompt: an explicit second click
  // before we burn credits across every brand in this org, since
  // overwrite=true wipes existing researched values rather than just
  // filling blanks.
  const [confirmingForce, setConfirmingForce] = useState(false);


  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
        <Loader2 size={18} className="spin" /> Counting incomplete brands…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px' }}>
        <AlertCircle size={18} /> {error}
      </div>
    );
  }
  return (
    <div>
      {total > 0 ? (
        <>
          <p style={{ fontSize: '0.95rem', marginBottom: '0.75rem' }}>
            <strong>{total}</strong> brand{total === 1 ? '' : 's'} in org{' '}
            <code>{orgId}</code> {total === 1 ? 'is' : 'are'} missing at least
            one structured field (country, hq_city, hq_state, founders,
            founded_year, parent_company).
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1rem' }}>
            <strong>Fill mode</strong> re-runs the deep-research pipeline
            (Perplexity primary, Brave + Kimi fallback) on every incomplete
            brand in this org. Operator-supplied values are preserved —
            only blank columns get filled. One credit per brand processed
            is debited from this org. The job runs in the background — you
            can close this modal and come back. Switch orgs to bulk-research
            a different tenant.
          </p>
        </>
      ) : (
        <p style={{ fontSize: '0.95rem', marginBottom: '0.75rem' }}>
          Every brand in org <code>{orgId}</code> has all structured fields
          populated — fill mode has nothing to do.
        </p>
      )}

      {sample.length > 0 && (
        <div style={{ marginBottom: '1rem', maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary, #f9fafb)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem 0.75rem' }}>Brand</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Missing</th>
              </tr>
            </thead>
            <tbody>
              {sample.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
                  <td style={{ padding: '0.4rem 0.75rem' }}>{row.name}</td>
                  <td style={{ padding: '0.4rem 0.75rem', color: 'var(--text-secondary)' }}>{row.missing.join(', ')}</td>
                </tr>
              ))}
              {total > sample.length && (
                <tr><td colSpan={2} style={{ padding: '0.4rem 0.75rem', color: 'var(--text-secondary)' }}>
                  …and {total - sample.length} more
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Force re-research panel — wipes existing researched values
          and rebuilds them from scratch via Perplexity. Used when
          past runs produced poor data (page-title brand names, wrong
          founders, etc.) and the operator wants the freshest info. */}
      <div style={{
        marginTop: '1rem',
        padding: '0.85rem 1rem',
        borderRadius: '8px',
        background: 'rgba(239, 68, 68, 0.06)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, color: '#b91c1c', marginBottom: '0.4rem' }}>
          <AlertCircle size={16} /> Force re-research all brands (overwrite mode)
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '0.75rem' }}>
          Targets <strong>all {totalAll}</strong> brand{totalAll === 1 ? '' : 's'} in
          org <code>{orgId}</code>. Existing social accounts stay active; proposed
          account changes go to Settings → Research changes for super-admin approval.
          Other research fields, including manual edits, may be refreshed.
          One credit per brand from this org.
        </p>
        {!confirmingForce ? (
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setConfirmingForce(true)}
            disabled={totalAll === 0}
          >
            <RefreshCw size={14} /> Force re-research {totalAll} brand{totalAll === 1 ? '' : 's'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              Other research data will be refreshed. Changes to existing social accounts require approval in Settings → Research changes. Continue?
            </span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setConfirmingForce(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              style={{ background: '#b91c1c' }}
              onClick={() => onStart('overwrite')}
            >
              <RefreshCw size={14} /> Yes, refresh all {totalAll}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
        {total > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => onStart('fill')}>
            <RefreshCw size={14} /> Fill blanks on {total} brand{total === 1 ? '' : 's'}
          </button>
        )}
      </div>
    </div>
  );
}


function BulkProgress({ job }) {
  const total = job.total_brands || 0;
  const processed = job.processed_brands || 0;
  const pct = total ? Math.round((processed / total) * 100) : 0;
  return (
    <div>
      <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
        Processing {processed} / {total} brand{total === 1 ? '' : 's'}…
      </div>
      <div style={{ height: '10px', background: 'var(--bg-secondary, #f3f4f6)', borderRadius: '999px', overflow: 'hidden', marginBottom: '1rem' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #667eea, #764ba2)', transition: 'width 0.3s' }} />
      </div>
      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <span><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> {job.succeeded} filled</span>
        <span>{job.no_data} no data</span>
        <span style={{ color: '#b91c1c' }}>{job.failed} failed</span>
      </div>
    </div>
  );
}


function BulkResult({ job, onClose }) {
  const summary = job.summary || [];
  const modeLabel = job.mode === 'overwrite' ? 'overwrite' : 'fill';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: '#059669' }}>
        <CheckCircle2 size={20} /> Processed {job.processed_brands} brand{job.processed_brands === 1 ? '' : 's'}
        <span style={{
          background: modeLabel === 'overwrite' ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.12)',
          color: modeLabel === 'overwrite' ? '#b91c1c' : '#4338ca',
          padding: '0.1rem 0.55rem',
          borderRadius: '999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          {modeLabel}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
        <span><strong>{job.succeeded}</strong> filled</span>
        <span><strong>{job.no_data}</strong> no data</span>
        <span style={{ color: '#b91c1c' }}><strong>{job.failed}</strong> failed</span>
      </div>
      {summary.length > 0 && (
        <div style={{ marginTop: '0.5rem', maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary, #f9fafb)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem 0.75rem' }}>Brand</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Status</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
                  <td style={{ padding: '0.4rem 0.75rem' }}>{row.brand_name}</td>
                  <td style={{ padding: '0.4rem 0.75rem' }}>
                    <BulkStatusBadge status={row.status} />
                  </td>
                  <td style={{ padding: '0.4rem 0.75rem', color: 'var(--text-secondary)' }}>{row.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}


function BulkStatusBadge({ status }) {
  const palette = {
    succeeded: { bg: 'rgba(16, 185, 129, 0.15)', fg: '#047857', label: 'Filled' },
    no_data: { bg: 'rgba(107, 114, 128, 0.15)', fg: '#374151', label: 'No data' },
    failed: { bg: 'rgba(239, 68, 68, 0.15)', fg: '#b91c1c', label: 'Failed' },
  };
  const p = palette[status] || palette.failed;
  return (
    <span style={{ background: p.bg, color: p.fg, padding: '0.15rem 0.55rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600 }}>
      {p.label}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Super-admin: force weekly social sync now (current org only)
// ─────────────────────────────────────────────────────────────────────────

export function ForceSocialSyncModal({ onClose, onCompleted }) {
  // Phases: 'preview' (eligible count + start button), 'running'
  // (progress poll), 'done' (final summary), 'error'.
  const [phase, setPhase] = useState('preview');
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  // Load preview on mount. If a job is already in flight for this
  // org, skip straight to polling so a super-admin who closed and
  // reopened the modal sees the live progress instead of being told
  // they can't start a new one.
  useEffect(() => {
    let cancelled = false;
    api.previewForceSocialSync()
      .then(data => {
        if (cancelled) return;
        setPreview(data);
        if (data.active_job) {
          setJob({ ...data.active_job, job_id: data.active_job.id });
          setPhase('running');
        }
      })
      .catch(err => { if (!cancelled) setError(err?.message || 'Failed to load preview'); });
    return () => { cancelled = true; };
  }, []);

  // Poll the job row every 2 s while it's running; stop on terminal status.
  useEffect(() => {
    if (phase !== 'running' || !job?.job_id) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api.getForceSocialSyncJob(job.job_id);
        if (cancelled) return;
        setJob({ ...next, job_id: job.job_id });
        if (next.status === 'completed' || next.status === 'failed') {
          setPhase(next.status === 'failed' ? 'error' : 'done');
          if (next.status === 'failed' && next.error_message) {
            setError(next.error_message);
          }
          if (onCompleted) onCompleted();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to poll job');
          setPhase('error');
        }
      }
    };
    const id = setInterval(tick, 2000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, job?.job_id, onCompleted]);

  const startRun = async () => {
    try {
      setError(null);
      const result = await api.startForceSocialSync();
      setJob(result);
      setPhase('running');
    } catch (err) {
      // If the server returned structured preflight blockers, refresh
      // the preview state so the modal goes back to the preview phase
      // with the blocker list visible — better than dumping the user
      // into a terminal error screen they can't act on.
      const blockers = err?.detail?.blockers;
      if (Array.isArray(blockers) && blockers.length > 0) {
        setPreview((prev) => ({ ...(prev || {}), blockers }));
        return;
      }
      setError(err?.message || 'Failed to start force sync');
      setPhase('error');
    }
  };

  const eligible = preview?.eligible_brands ?? 0;
  const orgId = preview?.org_id ?? job?.org_id ?? '';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-primary, #fff)', borderRadius: '12px',
          maxWidth: '720px', width: '94%', maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border-color, #e5e7eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: '1.25rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Zap size={20} /> Force Weekly Social Sync
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {phase === 'preview' && (
            <ForceSyncPreview
              loading={!preview && !error}
              eligible={eligible}
              orgId={orgId}
              blockers={preview?.blockers || []}
              error={error}
              onClose={onClose}
              onStart={startRun}
            />
          )}

          {phase === 'running' && job && (
            <ForceSyncProgress job={job} />
          )}

          {phase === 'done' && job && (
            <ForceSyncResult job={job} onClose={onClose} />
          )}

          {phase === 'error' && (
            <div>
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <AlertCircle size={18} /> {error || 'Force sync failed.'}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ForceSyncPreview({ loading, eligible, orgId, blockers, error, onClose, onStart }) {
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
        <Loader2 size={18} className="spin" /> Counting eligible brands…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', padding: '0.75rem 1rem', borderRadius: '8px' }}>
        <AlertCircle size={18} /> {error}
      </div>
    );
  }
  if (eligible === 0) {
    return (
      <div>
        <div style={{ marginBottom: '1rem' }}>
          No brands in this org have an Instagram or TikTok handle, so
          there's nothing to sync.
        </div>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
      </div>
    );
  }
  const hasBlockers = blockers && blockers.length > 0;
  return (
    <div>
      <p style={{ fontSize: '0.95rem', marginBottom: '0.75rem' }}>
        <strong>{eligible}</strong> brand{eligible === 1 ? '' : 's'} in org{' '}
        <code>{orgId}</code> {eligible === 1 ? 'has' : 'have'} a
        social handle and will be synced.
      </p>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1rem' }}>
        This runs the same job as the Sunday cron, scoped to the org
        you're currently viewing — pulling fresh follower counts and
        the most recent posts for every eligible brand. It bypasses
        the "already synced this week" guard, so a brand that ran on
        Monday will still run again. {eligible} credit{eligible === 1 ? '' : 's'} are
        deducted upfront in a single hub call (and refunded if the
        run aborts). The run takes a few minutes for typical org sizes;
        you can close this modal and reopen it to resume polling.
      </p>

      {hasBlockers && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.08)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#991b1b',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.85rem',
          lineHeight: 1.5,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, marginBottom: '0.4rem' }}>
            <AlertCircle size={16} /> Preflight failed — fix these before starting:
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary btn-sm"
          onClick={onStart}
          disabled={hasBlockers}
          title={hasBlockers ? 'Resolve the preflight blockers first' : undefined}
          style={hasBlockers ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
        >
          <Zap size={14} /> Run on {eligible} brand{eligible === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  );
}


function ForceSyncProgress({ job }) {
  // The orchestrator runs the whole pool to completion before
  // returning, so processed_brands stays at 0 until then. Show a
  // determinate bar based on total when the run finishes; until
  // then, an indeterminate look is more honest than a stuck 0%.
  const running = job.status === 'running' || job.status === 'pending';
  const total = job.total_brands || 0;
  return (
    <div>
      <div style={{ marginBottom: '0.75rem', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Loader2 size={16} className="spin" />
        Syncing {total} brand{total === 1 ? '' : 's'}…
      </div>
      <div style={{ height: '10px', background: 'var(--bg-secondary, #f3f4f6)', borderRadius: '999px', overflow: 'hidden', marginBottom: '1rem', position: 'relative' }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent 0%, #667eea 50%, transparent 100%)',
          animation: running ? 'force-sync-shimmer 1.5s linear infinite' : 'none',
          width: '40%',
        }} />
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
        Per-brand results land in the summary below once the run
        completes. Closing this modal won't cancel the job — reopen
        the panel to see the result.
      </p>
      <style>{`
        @keyframes force-sync-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
}


function ForceSyncResult({ job, onClose }) {
  const summary = job.summary || [];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: '#059669' }}>
        <CheckCircle2 size={20} /> Synced {job.processed_brands} brand{job.processed_brands === 1 ? '' : 's'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { label: 'IG ok', value: job.instagram_success, color: '#059669' },
          { label: 'TT ok', value: job.tiktok_success, color: '#059669' },
          { label: 'Posts', value: job.posts_synced, color: '#374151' },
          { label: 'Errors', value: job.errors, color: '#b91c1c' },
        ].map((c) => (
          <div key={c.label} style={{ background: 'var(--bg-secondary, #f9fafb)', borderRadius: '8px', padding: '0.5rem 0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
          </div>
        ))}
      </div>
      {summary.length > 0 && (
        <div style={{ marginTop: '0.5rem', maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary, #f9fafb)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem 0.75rem' }}>Brand</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>Instagram</th>
                <th style={{ padding: '0.5rem 0.75rem' }}>TikTok</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.brand_id} style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
                  <td style={{ padding: '0.4rem 0.75rem' }}>{row.brand_name}</td>
                  <td style={{ padding: '0.4rem 0.75rem' }}>
                    <PlatformCell result={row.instagram} />
                  </td>
                  <td style={{ padding: '0.4rem 0.75rem' }}>
                    <PlatformCell result={row.tiktok} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}


function PlatformCell({ result }) {
  if (!result) {
    return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  }
  if (result.error) {
    return (
      <span style={{ color: '#b91c1c' }} title={result.error}>
        {result.error.length > 40 ? `${result.error.slice(0, 40)}…` : result.error}
      </span>
    );
  }
  if (result.success) {
    return (
      <span style={{ color: '#059669' }}>
        {result.followers != null ? `${result.followers.toLocaleString()} followers` : 'ok'}
        {result.posts_synced ? ` · ${result.posts_synced} posts` : ''}
      </span>
    );
  }
  return <span style={{ color: 'var(--text-secondary)' }}>no handle</span>;
}


